import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { FC } from "react";
import { Bubble } from "~ui-components/components/molecules/legend/shapes/bubble";
import { Category } from "~ui-components/components/molecules/legend/shapes/category";
import { Quantity } from "~ui-components/components/molecules/legend/shapes/quantity";
import { Simple } from "~ui-components/components/molecules/legend/shapes/simple";
import {
	LAYER_STYLE_TYPE,
	LayerGroup as LayerGroupType,
	Layer as LayerType,
} from "~ui-components/types/__generated/gql/graphql";
import { MapToolDrawer } from "../map-tool-drawer";
import { RasterBar } from "./shapes/heatmap";

interface Props {
	onClose: () => void;
	layerGroups: LayerGroupType[];
}

export const Legend = ({ onClose, layerGroups = [] }: Props) => {
	return (
		<MapToolDrawer
			title="Legend"
			onClose={onClose}
			Content={layerGroups.map((layerGroup, key) => (
				<LayerGroup
					key={key}
					layerGroup={layerGroup}
				/>
			))}
			Action={<BaseMap />}
		/>
	);
};

const StyledBadge = styled(Badge)(({ theme }) => ({
	"& .MuiBadge-badge": {
		backgroundColor: "#44b700",
		color: "#44b700",
		boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
		"&::after": {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%",
			height: "100%",
			borderRadius: "50%",
			animation: "ripple 1.2s infinite ease-in-out",
			border: "1px solid currentColor",
			content: '""',
		},
	},
	"@keyframes ripple": {
		"0%": {
			transform: "scale(.8)",
			opacity: 1,
		},
		"100%": {
			transform: "scale(2.4)",
			opacity: 0,
		},
	},
}));

const BaseMap = () => {
	return (
		<>
			<Stack
				direction="row"
				spacing={1}>
				<StyledBadge
					overlap="circular"
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					variant="dot">
					<Avatar variant="rounded" />
				</StyledBadge>
				<Avatar variant="rounded" />
				<Avatar variant="rounded" />
				<Avatar variant="rounded" />
				<Avatar variant="rounded" />
				<Avatar variant="rounded" />
			</Stack>
		</>
	);
};

const LayerGroup: FC<{ layerGroup: LayerGroupType }> = ({ layerGroup }) => {
	return (
		<>
			<Stack
				direction="row"
				spacing={1}>
				<Stack
					direction="column"
					spacing={0}
					sx={{ flexGrow: 1 }}>
					{layerGroup.layerCollection.items.map((layer, index) => {
						const lastChild =
							index === layerGroup.layerCollection.items.length - 1;

						return (
							<>
								<Layer
									firstChild={!index}
									layer={layer}
								/>
								{!lastChild && (
									<Divider
										sx={{ borderColor: "var(--mui-palette-neutral-100)" }}
									/>
								)}
							</>
						);
					})}
				</Stack>
			</Stack>
			<Divider />
		</>
	);
};

const Layer: FC<{ layer: LayerType; firstChild: boolean }> = ({
	layer,
	firstChild,
}) => {
	return (
		<Stack
			direction="row"
			spacing={0}
			sx={{ p: 2 }}>
			<Stack
				direction="column"
				spacing={2}
				sx={{ flexGrow: 1 }}>
				<Stack spacing={0}>
					<Typography variant="subtitle1">{layer.name}</Typography>
				</Stack>

				{layer.styleType === LAYER_STYLE_TYPE.LINE_SIMPLE && (
					<Stack>
						<Simple style={layer.lineSimpleStyle} />
					</Stack>
				)}
				{layer.styleType === LAYER_STYLE_TYPE.POINT_SIMPLE && (
					<Stack>
						<Simple style={layer.pointSimpleStyle} />
					</Stack>
				)}
				{layer.styleType === LAYER_STYLE_TYPE.POLYGON_SIMPLE && (
					<Stack>
						<Simple style={layer.polygonSimpleStyle} />
					</Stack>
				)}

				{layer.styleType === LAYER_STYLE_TYPE.LINE_CATEGORY && (
					<Stack>
						<Category
							type={LAYER_STYLE_TYPE.LINE_CATEGORY}
							style={layer.lineCategoryStyle!}
						/>
					</Stack>
				)}
				{layer.styleType === LAYER_STYLE_TYPE.POINT_CATEGORY && (
					<Stack>
						<Category
							type={LAYER_STYLE_TYPE.POINT_CATEGORY}
							style={layer.pointCategoryStyle!}
						/>
					</Stack>
				)}
				{layer.styleType === LAYER_STYLE_TYPE.POLYGON_CATEGORY && (
					<Stack>
						<Category
							type={LAYER_STYLE_TYPE.POLYGON_CATEGORY}
							style={layer.polygonCategoryStyle!}
						/>
					</Stack>
				)}

				{layer.styleType === LAYER_STYLE_TYPE.LINE_QUANTITY && (
					<Stack>
						<Quantity style={layer.lineQuantityStyle!} />
					</Stack>
				)}
				{layer.styleType === LAYER_STYLE_TYPE.POINT_QUANTITY && (
					<Stack>
						<Quantity style={layer.pointQuantityStyle!} />
					</Stack>
				)}
				{layer.styleType === LAYER_STYLE_TYPE.POLYGON_QUANTITY && (
					<Stack>
						<Quantity style={layer.polygonQuantityStyle!} />
					</Stack>
				)}

				{layer.styleType === LAYER_STYLE_TYPE.BUBBLE && (
					<Bubble style={layer.bubbleStyle!} />
				)}

				{layer.styleType === LAYER_STYLE_TYPE.HEATMAP && (
					<Stack>
						<RasterBar />
						<Stack
							spacing={0}
							direction="row">
							<Typography
								sx={{ flexGrow: 1 }}
								color="text.secondary"
								variant="caption">
								Low
							</Typography>
							<Typography
								color="text.secondary"
								variant="caption">
								Hight
							</Typography>
						</Stack>
					</Stack>
				)}
				<Stack spacing={0}>
					<Typography
						color="text.secondary"
						variant="body2">
						this is hardcode description
						{layer.description}
					</Typography>
				</Stack>
			</Stack>
			{firstChild && (
				<Stack spacing={0}>
					<Checkbox
						checked={true}
						onChange={(event) => console.log("checked", event)}
					/>
				</Stack>
			)}
		</Stack>
	);
};
